"use client";

import { type PropsWithChildren, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { apiWorkspaceScopeKey } from "@/lib/api-workspace-scope";
import {
  ApiWorkspaceActorContext,
  MarketplaceWorkspaceSnapshotContext,
  MarketplaceWorkspacePersistenceContext,
  useRememberedMarketplacePath,
} from "@/hooks/use-marketplace-workspace";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { getApiQueryClientDefaults } from "@/lib/api-query-client";
import { httpBatchLink, splitLink } from "@trpc/react-query";
import superjson from "superjson";

type HttpBatchLinkOptions = Parameters<typeof httpBatchLink>[0];

type LinkFetch = NonNullable<HttpBatchLinkOptions["fetch"]>;

function isNetworkError(error: unknown): boolean {
  if (error instanceof Error) {
    const msg = error.message;
    return (
      msg.includes("Load failed") ||
      msg.includes("Failed to fetch") ||
      msg.includes("NetworkError") ||
      msg.includes("network error") ||
      msg.includes("ERR_NETWORK")
    );
  }
  return false;
}

const fetchWithRetry: LinkFetch = async (url, options) => {
  const maxRetries = 2;
  const init = options as RequestInit | undefined;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fetch(url, init);
    } catch (error) {
      if (attempt < maxRetries && isNetworkError(error)) {
        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
        continue;
      }
      throw error;
    }
  }
  return fetch(url, init);
};

export function ApiClientProvider({
  children,
  initialActorUserId,
  initialWorkspaceSnapshot = "",
  isDemo = false,
}: PropsWithChildren<{
  initialActorUserId?: string | null;
  initialWorkspaceSnapshot?: string;
  isDemo?: boolean;
}>) {
  const pathname = usePathname();
  const [actorUserId, setActorUserId] = useState(initialActorUserId ?? null);
  const rememberedPath = useRememberedMarketplacePath(
    actorUserId,
    initialWorkspaceSnapshot,
    !isDemo,
  );
  const requestPath = pathname.startsWith("/os/") ? pathname : rememberedPath;
  return (
    <ApiWorkspaceActorContext.Provider value={setActorUserId}>
      <MarketplaceWorkspacePersistenceContext.Provider value={!isDemo}>
        <MarketplaceWorkspaceSnapshotContext.Provider
          value={initialWorkspaceSnapshot}
        >
          <ScopedApiClientProvider
            key={`${actorUserId}:${apiWorkspaceScopeKey(requestPath ?? pathname)}`}
            requestPath={requestPath}
          >
            {children}
          </ScopedApiClientProvider>
        </MarketplaceWorkspaceSnapshotContext.Provider>
      </MarketplaceWorkspacePersistenceContext.Provider>
    </ApiWorkspaceActorContext.Provider>
  );
}

function ScopedApiClientProvider({
  children,
  requestPath,
}: PropsWithChildren<{ requestPath: string | null }>) {
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
  const requestPathRef = useRef(requestPath);
  requestPathRef.current = requestPath;

  const [queryClient] = useState(() => {
    const apiQueryClientDefaults = getApiQueryClientDefaults();
    return new QueryClient({
      defaultOptions: {
        ...apiQueryClientDefaults,
        queries: {
          ...apiQueryClientDefaults.queries,
          staleTime: () =>
            typeof window !== "undefined" &&
            window.location.pathname.startsWith("/os/")
              ? 5_000
              : 0,
          refetchOnWindowFocus: () =>
            typeof window === "undefined" ||
            !window.location.pathname.startsWith("/os/"),
          retry: (failureCount, error) => {
            if (isNetworkError(error)) {
              return failureCount < 2;
            }
            return false;
          },
        },
      },
    });
  });

  const [trpcClient] = useState(() => {
    const apiLinkOptions = {
      url: `${baseUrl}/api`,
      transformer: superjson,
      maxURLLength: 4096,
      fetch: fetchWithRetry,
      headers() {
        if (requestPathRef.current) {
          return { "x-os-url-path": requestPathRef.current };
        }
        return {};
      },
    };
    return api.createClient({
      links: [
        splitLink({
          condition: (op) => op.path === "auth.user",
          true: httpBatchLink({
            url: `${baseUrl}/api-lite`,
            transformer: superjson,
            maxURLLength: 4096,
            fetch: fetchWithRetry,
          }),
          // The pinned tRPC prerelease leaks AbortError rejections when its
          // JSONL consumer finishes. Keep queries buffered until that transport
          // is upgraded; this also preserves Set-Cookie on portal/intake calls.
          false: httpBatchLink(apiLinkOptions),
        }),
      ],
    });
  });

  return (
    <api.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </api.Provider>
  );
}
